import { FC, useEffect, useState } from "react";
import { Link as RouterLink, matchPath, useLocation } from "react-router-dom";

import { Link } from "@mui/material";

import { EnabledLanguages } from "@framework/constants";
import { useAsu } from "@framework/provider-asu";
import { Localization } from "@framework/provider-localization";
import { IUserDto, useUser } from "@framework/provider-user";
import { useApiCall } from "@framework/react-hooks";

import { Logo } from "./components/logo";
import { DateRange } from "./date-range";
import { Sections } from "./sections";
import { SoundAlarmButton } from "./sound-alarm";
import {
  StyledAppBar,
  StyledCustomerLogo,
  StyledCustomerLogoWrapper,
  StyledHeaderMiddle,
  StyledDBLogo,
  StyledDBLogoWrapper,
  StyledDBName,
  StyledHeaderWrapper,
  StyledLogoWrapper,
  StyledProfileWrapper,
  StyledToolbar,
} from "./styled";

export const Header: FC = () => {
  const { isMenuOpen, setDbTz } = useAsu();
  const user = useUser<IUserDto>();
  const isAuthenticated = user.isAuthenticated();
  const location = useLocation();

  const [DBName, setDBName] = useState<string>("");
  const [DBLogo, setDBLogo] = useState<string | null>(null);
  const [customerLogo, setCustomerLogo] = useState<string | null>(null);
  const isProfilePage =
    !!matchPath({ path: "/profile", end: true }, location.pathname) ||
    !!matchPath({ path: "/profile/:tab" }, location.pathname);
  const isUsersPage = !!matchPath({ path: "/users", end: true }, location.pathname);
  const isLoginPage = !!matchPath({ path: "/login", end: true }, location.pathname);

  const { fn: getDBName } = useApiCall(
    async (api): Promise<unknown> => {
      try {
        const json = await api.fetchJson({
          url: "/info/dbname",
        });
        return json as unknown;
      } catch {
        return undefined;
      }
    },
    { error: false, success: false },
  );

  const { fn: getDBLogo } = useApiCall(
    async (api): Promise<unknown> => {
      try {
        const json = await api.fetchJson({
          url: "/info/dblogo",
        });
        return json as unknown;
      } catch {
        return undefined;
      }
    },
    { error: false, success: false },
  );

  const { fn: getCustomerLogo } = useApiCall(
    async (api): Promise<unknown> => {
      try {
        const json = await api.fetchJson({
          url: "/info/customer-logo",
        });
        return json as unknown;
      } catch {
        return undefined;
      }
    },
    { error: false, success: false },
  );

  useEffect(() => {
    void getCustomerLogo().then((json: unknown) => {
      const data = json as { value?: string | null };
      if (data?.value) {
        setCustomerLogo(data.value);
      }
    });
  }, []);

  useEffect(() => {
    if (DBLogo === null && isAuthenticated) {
      void getDBLogo().then((json: unknown) => {
        const data = json as { value?: string };
        if (data?.value) {
          setDBLogo(data.value);
        }
      });
    }
  }, [DBLogo, isAuthenticated]);

  useEffect(() => {
    if (!DBName && isAuthenticated) {
      void getDBName().then((json: unknown) => {
        const rows = json as Array<{ value?: string; tz?: number }> | undefined;
        const data = rows?.[0];

        if (data?.value) {
          setDBName(data.value);
          document.title = data.value;
        }

        if (typeof data?.tz === "number") {
          setDbTz(data.tz);
        }
      });
    }
  }, [DBName, isAuthenticated]);

  return (
    <StyledAppBar elevation={0} position="fixed">
      <StyledToolbar>
        <StyledLogoWrapper isMenuOpen={isMenuOpen}>
          <Link component={RouterLink} to="/">
            <Logo />
          </Link>
        </StyledLogoWrapper>
        <StyledHeaderWrapper>
          <StyledDBLogoWrapper>
            {DBLogo ? (
              <StyledDBLogo component="img" src={`data:image/jpeg;charset=utf-8;base64,${DBLogo}`} />
            ) : (
              <StyledDBName>{DBName}</StyledDBName>
            )}
          </StyledDBLogoWrapper>
          <StyledHeaderMiddle>
            {isAuthenticated && !isProfilePage && !isUsersPage && !isLoginPage ? <DateRange /> : null}
          </StyledHeaderMiddle>
          {customerLogo && !isLoginPage ? (
            <StyledCustomerLogoWrapper>
              <StyledCustomerLogo alt="" component="img" src={`data:image/jpeg;charset=utf-8;base64,${customerLogo}`} />
            </StyledCustomerLogoWrapper>
          ) : null}
          <StyledProfileWrapper isAuthenticated={isAuthenticated}>
            {isAuthenticated && !isProfilePage && !isUsersPage ? <SoundAlarmButton /> : null}
            <Localization languages={Object.values(EnabledLanguages)} />
            <Sections />
          </StyledProfileWrapper>
        </StyledHeaderWrapper>
      </StyledToolbar>
    </StyledAppBar>
  );
};
